import store from '@/store/index.js'
import router from '@/router'
import * as api from './api'
import Vue from 'vue'
import i18n from '@/i18n'
import * as Analytics from '@/helpers/analytics.js'
import { isDEV } from '@/helpers/env'

/**
 * @deprecated Legacy wrapper. No longer serves original purpose of addins school_id scope.
 * Logs error to console if url is undefined.
 *
 * @param url
 * @returns URL
 */
export function provideURL(url) {
    if (url.includes('undefined')) {
        if (isDEV())
            console.error(new Error('Undefined in URL', { payload: { url } }))
    }
    return `${url}`
}

/**
 * Executes given function after 1500ms or executes 'reject' function,
 * if an amount of specified 'leftAttempts' equals 0
 * @param {*} fn Function to execute
 * @param {*} data
 * @param {*} url
 * @param {*} resolve Promise resolve function
 * @param {*} reject Promise reject function called when no attempts remain
 * @param {*} error
 * @param {*} leftAttempts Amount of times given function will execute
 */
export function repeatRequest(
    fn,
    data,
    url,
    resolve,
    reject,
    error,
    leftAttempts
) {
    if (leftAttempts == 0) {
        reject(error)
    } else {
        --leftAttempts
        setTimeout(() => {
            fn(fn, data, url, resolve, reject, error, leftAttempts)
        }, 1500)
    }
}

/**
 * Sends GET request and gets response from server.
 * Request will be repeated specified amount of time, unless it's successful.
 *
 * @param {*} url Request URL
 * @param {*} leftAttempts Amount of times request might be repeated
 * @returns Server's response
 */
export function getData(url, leftAttempts) {
    return new Promise((resolve, reject) => {
        api.get(provideURL(url), { quiet: true })
            .then(response => {
                resolve(response)
            })
            .catch(error => {
                if (
                    error &&
                    error.message &&
                    error.message.includes('timeout')
                ) {
                    repeatRequest(
                        repeatRequest,
                        '',
                        url,
                        resolve,
                        reject,
                        error,
                        leftAttempts
                    )
                } else {
                    repeatRequest(
                        repeatRequest,
                        '',
                        url,
                        resolve,
                        reject,
                        error,
                        0
                    )
                }
            })
    })
}

/**
 * Sends GET request and gets response from server.
 * Request will be repeated 3 times, unless it's successful.
 * @param {*} url Request URL
 * @returns Server's response
 */
export function getDataWidthRetry(url) {
    const leftAttempts = 3
    return getData(url, leftAttempts)
}

/**
 * Shows toast with error notification that disappear after 4000ms
 * @param msg Message displayed in toast
 */
function errorNotif(msg) {
    Vue.notify({
        group: 'error',
        title: i18n.t('TOAST_ERROR_TITLE'),
        duration: 4000,
        text: msg
    })
}

/**
 * Handles error based it's message and enviroment.
 * In DEV it will console log error
 * @param {*} error New error
 * @param {*} [errors] Array of already existing errors
 * @param {*} [quiet] Allows to return error if false
 * @returns Error if quiet is false
 */
export function handleError(error, errors, quiet) {
    if (isDEV()) console.error(error)
    if (quiet == true) return error
    if (error && error.response) {
        getErrorMessage(error)
        const causes =
            error.response.data && error.response.data.error.cause
                ? Object.keys(error.response.data.error.cause)
                : []
        if (causes.length > 0 && errors) {
            errors.errors = causes.map(cause => {
                return {
                    name: cause,
                    message: error.response.data.error.cause[cause]
                }
            })
        }
    } else {
        if (error.message && error.message.includes('timeout')) {
            errorNotif(i18n.t('TOAST_DOWNLOADING_DATA_FAILED'))
        } else if (error.message == 'Network Error') {
            errorNotif(i18n.t('PROBLEM_WITH_INET_OCCURRED'))
        } else {
            Analytics.unknownError777(error)
        }
    }
}

/**
 * Extracts message from error and displays toasts with this message
 *
 * @param error
 * @returns
 */
function getErrorMessage(error) {
    const { data } = error.response
    if (!data || !data.error) {
        return errorNotif()
    }
    if (data.error.msg) {
        return errorNotif(data.error.msg)
    }
    if (data.error.cause) {
        const causes = Object.keys(data.error.cause).map(
            key => data.error.cause[key]
        )
        if (causes.length == 1) {
            return errorNotif(causes[0])
        } else {
            return errorNotif()
        }
    } else {
        return errorNotif()
    }
}

/**
 * Gets name of enviroment variable for given gateway
 *
 * @param gateWay 'api' | 'assets' | 'sockets'
 * @returns Name of enviroment variable
 */
function getGatewayVar(gateWay) {
    switch (gateWay) {
        case 'api':
            return 'VUE_APP_API_URL'
        case 'assets':
            return 'VUE_APP_ASSETS_URL'
        case 'sockets':
            return 'VUE_APP_SOCKETS_URL'
    }
}

/**
 * Gets base URL for given gateway and other variables:
 * - enviroment
 * - region
 * @param gateway 'api' | 'assets' | 'sockets'
 * @returns Base URL
 */
export function getApiBaseUrl(gateway) {
    const apiGateway = getGatewayVar(gateway)
    const defaultEnv = process.env.VUE_APP_API_ENV
    const env = window.localStorage.livekid_env || defaultEnv
    const useLocalhost = window.localStorage.use_localhost
    const envToUse = env && env != 'production' ? `${env}.` : ''
    const envGateway = process.env[apiGateway] || ''
    if (useLocalhost && gateway == 'api') return 'http://localhost:3001/'
    if (useLocalhost && gateway == 'sockets')
        return 'ws://localhost:3001/v1/socket'

    const region = store ? store.getters.getRegion() : 'pl'
    return envGateway.replace('{region}', `${region}.`).replace(
        '{env}',
        envToUse
    )
}
